export default {
  actions: {
    directions: 'Directions',
    contact: 'Contact',
    calendar: 'Calendar',
    cancel: 'Cancel',
    change: 'Change',
  },
  checklist: {
    title: 'Next steps:',
    paperwork: {
      title: {
        paperworkComplete: 'Your paperwork is complete.',
        paperworkIncomplete: 'Complete paperwork',
      },
      subtitle:
        'Please fill out your information before your visit. We’ll save the details for next time.',
      action: {
        text: 'Start paperwork',
      },
    },
    payment: {
      title: 'Add credit card information',
      subTitle: 'You won’t be charged until after your vist.',
      action: {
        text: 'Add credit card',
      },
    },
    checkInFrontDesk: {
      title: 'Check-in',
      subTitle: 'Please check-in when you arrive.',
    },
    checkInOnline: {
      title: 'Check-in online',
      subTitle: 'Use your mobile device to let the clinic know when you’ve arrived.',
      action: {
        text: 'Start online check-in',
      },
    },
    placeInLine: {
      title: 'View your place in line',
      subTitle:
        'After you check in, you’ll get a link to the waitlist to track your place in line.',
      action: {
        text: 'View live waitlist',
      },
    },
    videoVisit: {
      title: 'Join video visit',
      bookAheadTitle: 'Join video visit on {{date, moment::MMM D [at] h:mm A}}',
      subTitle: {
        defaultText: 'You will receive a text reminder 10 minutes before your visit time.',
        providerGroupBookingText:
          '{{providerName}}’s office will email you with instructions on how to join your video visit.',
        plusAsap:
          'Your {{MEMBERSHIP_NAME}} provider should join your call within the next 15 minutes.',
        plusBookAhead: `Join 5 minutes before your visit is set to start. You'll recieve a text message reminder.`,
      },
      action: {
        text: 'Enter virtual waiting room',
      },
    },
  },
  postActions: {
    saveMyInformation: {
      ctaText: 'Save my information',
      header: 'Book faster next time',
      subHeader: 'Save your information on Solv to make booking your next visit even easier.',
    },
    bookAgain: {
      ctaText: 'Book Again',
      header: 'Book another visit',
      subHeader: 'Re-book on Solv to get reminders and manage all your care in one place.',
    },
    leaveReview: {
      ctaText: 'Leave a review',
      header: 'How was your experience?',
      subHeader:
        'Thanks for choosing {{locationName}} for your care. We’d love to hear how it went.',
      plusSubHeader: `Thanks for choosing Solv Plus. We'd love to hear how it went.`,
    },
  },
  appointmentDetails: {
    locationNameAndCity: '{{locationName}} {{city}}',
    appointmentDateCopy: '{{appointmentDateCopy}}',
    membershipName: '{{MEMBERSHIP_NAME}} - Video visit',
    nextAvailable: 'Next available',
    asapBooking: 'ASAP - Within {{TELEMED_SLA_MINUTES}} minutes',
    telemedicineVisit: 'Telemedicine visit',
  },
  appointmentFootnote:
    '{{locationName}} uses Solv to help patients schedule visits, send helpful appointment reminders, and make checking in easier.',
  bookingStatus: {
    isPending: 'REQUEST RECEIVED',
    isCanceledVisit: '{{firstName}}’s visit was canceled',
    isPostVisit: '{{firstName}}’s visit is complete',
    isCheckedIn: '{{firstName}} is checked in',
    default: '{{firstName}}’s visit is booked!',
  },
  callClinic: {
    withPhoneNumber: {
      cancel: 'Please call the office at <0>{{phone}}</0> to cancel',
      reschedule: 'Please call the office at <0>{{phone}}</0> to reschedule',
    },
    withoutPhoneNumber: {
      cancel: 'Please call the office to cancel',
      reschedule: 'Please call the office to reschedule',
    },
  },
  error: {
    title: 'Oops, something went wrong',
    action: 'Call office',
    withPhoneNumber: {
      cancel:
        'We experienced some issues when trying to cancel your appointment. Please call the office at {{phone}}',
      reschedule:
        'We experienced some issues when trying to reschedule your appointment. Please call the office at {{phone}}',
    },
    withoutPhoneNumber: {
      cancel:
        'We experienced some issues when trying to cancel your appointment. Please call the office',
      reschedule:
        'We experienced some issues when trying to reschedule your appointment. Please call the office',
    },
  },
  confirmation: {
    reschedule: 'Your appointment has been rescheduled!',
  },
  confirmationVisitGuidelines: {
    covidTimeToResults: {
      pcrTest: {
        fifteenMinutes: 'For a COVID-19 PCR test, turn around time for results is 15 minutes.',
        lessThanOneHour:
          'For a COVID-19 PCR test, turn around time for results is less than one hour.',
        sameDay: 'For a COVID-19 PCR test, turn around time for results is same day.',
        oneToThreeDays: 'For a COVID-19 PCR test, turn around time for results is 1-3 days.',
        oneToFiveDays: 'For a COVID-19 PCR test, turn around time for results is 1-5 days.',
        sixPlusDays: 'For a COVID-19 PCR test, turn around time for results is 6+ days.',
      },
      rapidPcrTest: {
        fifteenMinutes:
          'For a rapid COVID-19 PCR test, turn around time for results is 15 minutes.',
        lessThanOneHour:
          'For a rapid COVID-19 PCR test, turn around time for results is less than one hour.',
        sameDay: 'For a rapid COVID-19 PCR test, turn around time for results is same day.',
        oneToThreeDays: 'For a rapid COVID-19 PCR test, turn around time for results is 1-3 days.',
        oneToFiveDays: 'For a rapid COVID-19 PCR test, turn around time for results is 1-5 days.',
        sixPlusDays: 'For a rapid COVID-19 PCR test, turn around time for results is 6+ days.',
      },
      antigenTest: {
        fifteenMinutes: 'For a COVID-19 antigen test, turn around time for results is 15 minutes.',
        lessThanOneHour:
          'For a COVID-19 antigen test, turn around time for results is less than one hour.',
        sameDay: 'For a COVID-19 antigen test, turn around time for results is same day.',
        oneToThreeDays: 'For a COVID-19 antigen test, turn around time for results is 1-3 days.',
        oneToFiveDays: 'For a COVID-19 antigen test, turn around time for results is 1-5 days.',
        sixPlusDays: 'For a COVID-19 antigen test, turn around time for results is 6+ days.',
      },
      rapidAntigenTest: {
        fifteenMinutes:
          'For a rapid COVID-19 antigen test, turn around time for results is 15 minutes.',
        lessThanOneHour:
          'For a rapid COVID-19 antigen test, turn around time for results is less than one hour.',
        sameDay: 'For a rapid COVID-19 antigen test, turn around time for results is same day.',
        oneToThreeDays:
          'For a rapid COVID-19 antigen test, turn around time for results is 1-3 days.',
        oneToFiveDays:
          'For a rapid COVID-19 antigen test, turn around time for results is 1-5 days.',
        sixPlusDays: 'For a rapid COVID-19 antigen test, turn around time for results is 6+ days.',
      },
    },
    locationPerformingCopy:
      'Please <0>call the clinic</0> for more details on what to expect during COVID testing.',
    genericSampleCollectionCopy:
      'Clinic staff will collect a sample by a nasal swab, mouth swab, or saliva test, depending on the type of testing available at the clinic.',
    bookAhead: {
      title: 'Tips for a smooth visit:',
      lineOne: 'Arrive 10 minutes early, especially if you’re a new patient.',
      lineTwo: 'Make a list of questions you have for your provider and bring a way to take notes.',
      lineThree: 'Bring your government-issued photo ID.',
      lineFour: 'If you’re using insurance, take your insurance card with you.',
      lineFive: 'Bring your debit or credit card for any fees due at time of service.',
    },
    covidTestOnly: {
      title: 'Know before you go:',
      lineOne: 'Bring a government issued photo ID.',
      lineTwo: 'The clinic will provide information on how you’ll receive your results.',
    },
    covidVaccineBookAhead: {
      title: 'Tips for a smooth visit:',
      lineOne:
        'Wear loose-fitting or sleeveless clothing so healthcare workers can access your arm.',
      lineTwo:
        'Be sure you’re hydrated and limit your intake of alcohol the night before your appointment.',
      lineThree:
        'It is not recommended you take over-the-counter medicines (ie: Tylenol, Advil) to try and prevent vaccine-related side effects.',
      lineFour: 'Bring a photo ID and proof of your elegibility.',
      lineFive:
        'If your vaccine requires multiple doses, be sure to confirm when and how to book your next dose with the staff on site.',
    },
    videoVisitBookAhead: {
      title: 'Tips for a smooth video visit:',
      lineOne: 'Bring a government-issued photo ID.',
      lineTwo: 'Make sure you have a strong Wi-Fi connection on your device.',
      lineThree:
        'Find a quiet, private place with good lighting to take your visit. If possible, use headphones.',
      lineFour: 'Make a list of questions you have for your provider and have a way to take notes.',
      lineFive: 'Join the call 10 minutes early, especially if you’re a new patient.',
    },
    pgInPerson: {
      title: 'Tips for a smooth visit:',
      lineOne: 'Arrive 10 minutes early, especially if you’re a new patient.',
      lineTwo: 'Make a list of questions you have for your provider and bring a way to take notes.',
      lineThree: 'Bring your government-issued photo ID.',
      lineFour: 'If you’re using insurance, have your insurance card with you.',
      lineFive: 'Bring your debit or credit card for any fees due at time of service.',
    },
    pgTelemed: {
      title: 'Tips for a smooth visit:',
      lineOne: 'Arrive 10 minutes early, especially if you’re a new patient.',
      lineTwo: 'Make a list of questions you have for your provider and bring a way to take notes.',
      lineThree: 'Bring your government-issued photo ID.',
      lineFour: 'If you’re using insurance, have your insurance card with you.',
      lineFive: 'Bring your debit or credit card for any fees due at time of service.',
    },
    solvPlusTelemed: {
      title: 'Tips for a smooth video visit:',
      lineOne: 'Make sure you have a strong Wi-Fi connection on your device.',
      lineTwo:
        'Find a quiet, private place with good lighting to take your visit. If possible, use headphones.',
      lineThree:
        'Make a list of questions you have for your provider and have a way to take notes.',
      lineFour: 'Join the call a few minutes early',
    },
    solvPlusInPerson: {
      title: 'Tips for a smooth visit:',
      lineOne: `Arrive 10 minutes early, especially if you're a new patient.`,
      lineTwo: 'Make a list of questions you have for your provider and bring a way to take notes.',
      lineThree: 'Bring your government-issued photo ID.',
    },
    walkIn: {
      title: '',
    },
  },
};
